// The "Stores" tab of the map's side panel: the stores currently on the map as a virtualized, keyboard-navigable list
// (pins are drawn on a canvas and cannot take focus, features/common/README.md). Typing narrows the list with the
// Stores search (`searchRecords`); choosing a row flies the map to the store and opens its peek, like picking a
// record in the iOS map's search sheet. In the map's select mode the rows get checkboxes bound to the map's selection
// (pins are canvas-only, so this list is the keyboard and screen-reader way to pick stores): Enter or a click toggles
// the row like its pin, Space and Shift-click work as in every table, and the header box takes every listed row.

import { useMemo, useState } from 'react';
import { recordDisplayName, type RecordPayload } from '@/contracts/models';
import { formatGrouped } from '@/domain/format';
import { recordSubtitle, searchRecords } from '@/domain/records';
import { compareLocalized, idKey } from '@/domain/util';
import { ColorDot, DataTable, EmptyState, SearchField, createColumnHelper, type DataTableColumn } from '@/ui';
import { SearchX } from 'lucide-react';

interface Row {
  id: string;
  name: string;
  subtitle: string;
  color: string;
}

const col = createColumnHelper<Row>();

export interface MapStoreListProps {
  /** The stores on the map (filtered, located). */
  stores: readonly RecordPayload[];
  /** Pin color by lower-case id (so the dot matches the pin). */
  colors: ReadonlyMap<string, string>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Select mode: the map's selection (lower-case ids, as `idKey`), shown as checked rows. */
  selection?: ReadonlySet<string>;
  onSelectionChange?: (ids: Set<string>) => void;
}

export function MapStoreList({ stores, colors, selectedId, onSelect, selection, onSelectionChange }: MapStoreListProps) {
  const [query, setQuery] = useState('');
  const sorted = useMemo(
    () =>
      [...stores]
        .map((r) => ({ r, name: recordDisplayName(r) }))
        .sort((a, b) => compareLocalized(a.name, b.name))
        .map((x) => x.r),
    [stores],
  );
  const rows = useMemo<Row[]>(
    () =>
      searchRecords(sorted, query).map((r) => ({
        id: r.id,
        name: recordDisplayName(r),
        subtitle: recordSubtitle(r),
        color: colors.get(idKey(r.id)) ?? '#8C919E',
      })),
    [sorted, query, colors],
  );
  const columns = useMemo<DataTableColumn<Row>[]>(
    () => [
      col.accessor('name', {
        header: `${formatGrouped(rows.length)} ${rows.length === 1 ? 'store' : 'stores'}`,
        enableSorting: false,
        meta: { grow: 1 },
        size: 200,
        cell: (c) => {
          const row = c.row.original;
          return (
            <span className="flex min-w-0 items-center gap-2.5">
              <ColorDot color={row.color} size={12} className="ring-1 ring-white dark:ring-surface" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-fg">{row.name}</span>
                {row.subtitle !== '' && <span className="block truncate text-xs text-fg-muted">{row.subtitle}</span>}
              </span>
            </span>
          );
        },
      }),
    ],
    [rows.length],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <SearchField value={query} onValueChange={setQuery} label="Search stores on the map" placeholder="Search name, address, phone…" size="sm" />
      <div className="min-h-0 flex-1">
        <DataTable
          data={rows}
          columns={columns}
          // Lower-case, so the checked rows are the map's selection set as it is.
          getRowId={(r) => idKey(r.id)}
          label="Stores on the map"
          enableSorting={false}
          rowHeight={56}
          activeRowId={selectedId}
          onRowClick={(r) => onSelect(r.id)}
          {...(selection ? { selectable: true, selectedIds: selection, ...(onSelectionChange ? { onSelectionChange } : {}) } : {})}
          emptyState={
            <EmptyState
              compact
              icon={<SearchX />}
              title="No matches"
              message={query.trim() === '' ? 'No stores on the map. Adjust or reset the filters.' : `No store on the map matches "${query.trim()}".`}
            />
          }
        />
      </div>
    </div>
  );
}
